import React, { useState, useEffect } from 'react'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import * as Recipes from 'transfix-ui/components/Autosuggest'
import {
  Autosuggest,
  getAutosuggestOptions,
  IAutosuggestWrapperProps,
} from 'transfix-ui/components/Autosuggest'
import { useDebouncedCallback } from 'use-debounce/lib'
import {
  callSearchRequest,
  INameSearchResult,
  SEARCH_TYPE,
} from 'apps/marketplace/utils/api/search'
import { TextInputIconType } from 'components/atoms'

interface IAdminAutoSuggestProps extends IAutosuggestWrapperProps<INameSearchResult> {
  icon?: keyof typeof TextInputIconType
  /**
   * Render a custom option: 'default | 'withAvatar'
   */
  renderOptionType?: Recipes.RenderOptionType
  /**
   * Render a custom `startAdornment`: 'default | 'withAvatar'
   */
  startAdornmentType?: Recipes.StartAdornmentType
  useParentId?: boolean
  placeholder?: string
  searchType?: SEARCH_TYPE
  carrierApplicationId?: number
}

/**
 * Autosuggest search for transfix admin users, returns parent user id + name
 */
const AdminAutosuggest = ({
  onChange,
  icon = 'people',
  id: idProp = 'admin-user',
  value,
  renderOptionType = 'default',
  startAdornmentType = 'default',
  useParentId = true,
  placeholder = 'Select Rep Name',
  searchType = SEARCH_TYPE.Admin,
  carrierApplicationId,
}: IAdminAutoSuggestProps) => {
  const getValue = (val: Partial<INameSearchResult> | undefined): IAutocompleteOption => {
    if (val) {
      return {
        id: val.id || '',
        text: val.name,
        groupIndex: 0,
      }
    }
    return { id: '-1', text: '', groupIndex: 0 }
  }

  const [searchResults, setSearchResults] = useState<IAutocompleteOption[] | undefined>([
    getValue(value),
  ])
  const [isLoading, setIsLoading] = useState(false)

  // Get initial value and store in state
  const [initialValue, setInitialValue] = useState<IAutocompleteOption>(getValue(value))

  // Update if a new/different initial prop is passed in
  // Should only run when defaultValue changes
  useEffect(() => {
    setInitialValue(getValue(value))
    setSearchResults([getValue(value)])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const fetchUsers = (searchTerm: string) => {
    if (searchType === SEARCH_TYPE.CarrierUsers || SEARCH_TYPE.Dispatcher) {
      return callSearchRequest({
        searchType: {
          type: searchType,
          carrierApplicationId,
        },
        value: searchTerm,
      })
    }
    return callSearchRequest({
      searchType: {
        type: SEARCH_TYPE.Admin,
      },
      value: searchTerm,
    })
  }

  const changeHandler = async (searchTerm?: string) => {
    if (!searchTerm) {
      setSearchResults([])
      return
    }

    setIsLoading(true)
    const results = await fetchUsers(searchTerm)

    const mapOption = ({
      email,
      id: userId,
      name: text,
      parentUserId,
    }: {
      parentUserId: string
      id: string
      name: string
      email: string
    }) => ({
      id: useParentId ? parentUserId : userId,
      email,
      text,
    })

    const options = getAutosuggestOptions({
      options: results.namesearch,
      groupIndex: 1,
      mapOption,
    })
    setSearchResults(options)
    setIsLoading(false)
  }

  const [debouncedChangeHandler] = useDebouncedCallback((val: string) => {
    changeHandler(val)
  }, 500)

  const onChangeProp = (result?: IAutocompleteOption) => {
    if (result?.id && result.text) {
      const { email, id, text } = result
      onChange({
        id,
        name: text,
        email,
      })
    } else {
      onChange(undefined)
    }
  }

  return (
    <Autosuggest
      icon={icon}
      renderOptionType={renderOptionType}
      startAdornmentType={startAdornmentType}
      id={idProp}
      dataTest='admin-user-autosuggest'
      placeholder={placeholder}
      onChange={onChangeProp}
      onInputChange={debouncedChangeHandler}
      options={searchResults}
      isLoading={isLoading}
      value={initialValue}
      clearable
    />
  )
}

export default AdminAutosuggest
